<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb 
          parentRouteName="RewardCardList" 
          :extra-items="[
            {
              text: rewardCard.title,
              to: 'RewardCardView',
              params: $route.params.reward_card_id
            },
            '集點任務'
          ]" 
        />
      </div>
    </div>

    <b-card>
      <div class="d-flex align-items-center justify-content-between mb-2">
        <h4 class="font-weight-bold">
          <template v-if="rewardCard"> 【{{ rewardCard.title }}】 </template>
          集點任務管理
        </h4>
        <div class="d-flex align-items-center">
          <b-button
            class="flex-shrink-0 mr-2"
            variant="secondary"
            @click="$refs.allMissionsModal.show()"
          >
            <span class="mdi mdi-qrcode"></span> 全任務核銷碼
          </b-button>
          <b-button
            v-if="checkPermission([consts.REWARD_MISSION_CREATE])"
            class="flex-shrink-0 mr-2"
            variant="primary"
            :to="{ name: 'RewardMissionCreate' }"
            ><i class="fa fa-plus"></i>新增集點任務</b-button
          >
        </div>
      </div>

      <b-card-text>
        <b-overlay :show="showLoading" rounded="sm">
          <b-table
            striped
            hover
            responsive
            :items="rewardMissions"
            :fields="fields"
            @sort-changed="handleSort"
          >
            <template #cell(start_end_time)="data">
              <template v-if="data.item.period_type == 'custom'">
                <div>
                  {{ dateFormatter(data.item.start_at) }}
                </div>
                <div>
                  {{ dateFormatter(data.item.end_at) }}
                </div>
              </template>
              <template v-else>
                <div>
                  {{ dateFormatter(rewardCard.start_at) }}
                </div>
                <div>
                  {{ dateFormatter(rewardCard.end_at) }}
                </div>
              </template>
            </template>
            <template #cell(actions)="data">
              <copy-url-button
                class="mr-1"
                :url="generateLiffUrl(`${getMissionUrl(data.item)}`)"
                :title="data.item.title"
              >
                <template #qrcode>
                  <div class="d-flex flex-column align-items-center">
                    <vue-qr
                      :text="generateLiffUrl(`${getMissionUrl(data.item)}`)"
                      ref="qrcode"
                    ></vue-qr>
                  </div>
                </template>
                <template #qrcode-download-button>
                  <b-button
                    variant="primary"
                    @click="downloadQRCode(data.item.title)"
                  >
                    下載QRCode
                  </b-button>
                </template>
              </copy-url-button>
              <b-button
                class="mr-1"
                variant="inverse-info"
                :to="{
                  name: 'RewardMissionPointLog',
                  params: {
                    reward_card_id: rewardCardId,
                    reward_mission_id: data.item.id,
                  },
                }"
              >
                <span class="mdi mdi-account-multiple"></span> 點數紀錄
              </b-button>
              <b-button
                class="mr-1"
                v-if="checkPermission([consts.REWARD_MISSION_LIST_VIEW])"
                variant="inverse-primary"
                :to="{
                  name: 'RewardMissionView',
                  params: {
                    reward_card_id: rewardCardId,
                    reward_mission_id: data.item.id,
                  },
                }"
              >
                <span class="mdi mdi-eye"></span> 查看
              </b-button>
              <b-button
                class="mr-1"
                v-if="checkPermission([consts.REWARD_MISSION_MODIFY])"
                variant="inverse-warning"
                :to="{
                  name: 'RewardMissionEdit',
                  params: {
                    reward_card_id: rewardCardId,
                    reward_mission_id: data.item.id,
                  },
                }"
              >
                <span class="mdi mdi-lead-pencil"></span> 編輯
              </b-button>
              <b-button
                class="mr-1"
                v-if="checkPermission([consts.REWARD_MISSION_DELETE])"
                variant="inverse-danger"
                @click="deleteRewardMission(data.item)"
              >
                <span class="mdi mdi-delete"></span> 刪除
              </b-button>
            </template>
          </b-table>
        </b-overlay>
        <CustomPagination
          :currentPage.sync="query.page"
          :totalRows="totalRows"
          :perPage.sync="query.per_page"
        />
      </b-card-text>
    </b-card>

    <b-modal ref="allMissionsModal" footer-class="d-flex justify-content-center">
      <template #modal-header="{ close }">
        <h5 class="m-0 align-self-center">
          <template v-if="rewardCard">【{{ rewardCard.title }}】</template>全任務核銷碼
        </h5>
        <b-button size="sm" variant="link" class="text-dark" @click="close()">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" style="height: 20px">
            <path d="M310.6 361.4c12.5 12.5 12.5 32.75 0 45.25C304.4 412.9 296.2 416 288 416s-16.38-3.125-22.62-9.375L160 301.3L54.63 406.6C48.38 412.9 40.19 416 32 416S15.63 412.9 9.375 406.6c-12.5-12.5-12.5-32.75 0-45.25l105.4-105.4L9.375 150.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L160 210.8l105.4-105.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-105.4 105.4L310.6 361.4z"/>
          </svg>
        </b-button>
      </template>
      <b-textarea
        ref="allMissionsUrl"
        v-b-tooltip.click="'已複製'"
        :value="allMissionsUrl"
        @click="copyAllMissionsUrl"
        class="form-control"
        readonly
      ></b-textarea>
      <div class="d-flex flex-column align-items-center mt-3">
        <vue-qr :text="allMissionsUrl" ref="allMissionsQrcode"></vue-qr>
      </div>
      <template #modal-footer>
        <div class="modal-button-group">
          <div class="center-button">
            <b-button variant="primary" @click="downloadAllMissionsQRCode">下載QRCode</b-button>
          </div>
          <b-button variant="danger" @click="$refs.allMissionsModal.hide()">關閉</b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import { format } from "date-fns";
import rewardCardApi from "@/apis/reward-card";
import rewardMissionApi from "@/apis/reward-mission";
import CopyUrlButton from "@/components/Dashboard/CopyUrlButton.vue";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import generateLiffUrl from "@/mixins/Dashboard/liffUrlHelper";
import sweetAlertPresets from "@/mixins/Dashboard/sweetAlertPresets";
import { paginationMixin } from "@/mixins/pagination";
import deepGet from "@/utils/deepGet";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import VueQr from "vue-qr";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: {
    CopyUrlButton,
    CustomPagination,
    VueQr,
    BreadCrumb
  },
  mixins: [generateLiffUrl, sweetAlertPresets, paginationMixin],
  data() {
    return {
      consts: consts,
      showLoading: true,
      rewardCard: {},
      rewardMissions: [],
      fields: [
        { key: "title", label: "任務名稱", sortable: true },
        { key: "start_end_time", label: "使用期間" },
        { key: "earn_point", label: "可累點數" },
        { key: "actions", label: "管理" },
      ],
      totalRows: 0,
      initialized: false,
      query: {
        per_page: 10,
        page: 1,
        sort_by: "created_at",
        order_by: "desc",
      },
    };
  },
  computed: {
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
    rewardCardId() {
      return this.$route.params.reward_card_id;
    },
    allMissionsUrl() {
      return this.generateLiffUrl(`rewards-card/${this.rewardCardId}/all-mission`);
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route() {
      if (!this.initialized) return;
      this.fetchRewardMissions();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  async mounted() {
    await Promise.all([this.fetchRewardCard(), this.fetchRewardMissions()]);
    this.initialized = true;
    this.showLoading = false;
  },
  methods: {
    deepGet,
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    async fetchRewardCard() {
      const { data } = await rewardCardApi.get(this.rewardCardId);

      this.rewardCard = data.data;
    },
    async fetchRewardMissions() {
      try {
        const params = { ...this.query };
        const response = await rewardMissionApi.list(this.rewardCardId, params);
        this.rewardMissions = response.data.data;
        this.totalRows = response.data.meta.total;
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取集點任務列表失敗", "error");
      }
    },
    async deleteRewardMission(rewardMission) {
      this.showLoading = true;
      try {
        if (
          false ===
          (await this.confirmDeletion({
            text: `集點任務名稱：${rewardMission.title}`,
          }))
        ) {
          return;
        }

        await rewardMissionApi.remove(this.rewardCardId, rewardMission.id);
        this.$swal("刪除成功", "", "success");
        await this.fetchRewardMissions();
      } catch (error) {
        console.error(error);
        this.$swal("刪除失敗", "", "error");
      } finally {
        this.showLoading = false;
      }
    },
    handleSort(ctx) {
      this.query.sort_by = ctx.sortBy;
      this.query.order_by = ctx.sortDesc ? "desc" : "asc";
      this.handlePageReset();
    },
    handlePageReset() {
      const currentPage = parseInt(this.query.page, 10);

      if (currentPage === 1) {
        updateUrl(this.query, this.$store, this.$router);
      } else {
        this.query.page = 1;
      }
    },
    dateFormatter: (value) => {
      if (!value) return "無";
      return format(new Date(value), "yyyy-MM-dd");
    },
    downloadQRCode(filename) {
      const base64QRCodeImage = this.$refs?.qrcode?.$el?.src;
      if (base64QRCodeImage) {
        const temporaryDownloadLink = document.createElement("a");
        temporaryDownloadLink.href = base64QRCodeImage;
        temporaryDownloadLink.download = filename
          ? `${filename}.png`
          : "qrcode.png";
        temporaryDownloadLink.style.display = "none";
        document.body.appendChild(temporaryDownloadLink);
        temporaryDownloadLink.click();
      }
    },
    getMissionUrl(mission) {
      return mission.config.confirm_type === 'dynamic_verification'
        ? `rewards-card/mission/${mission.id}/dynamic-verification`
        : `rewards-card/mission/${mission.id}`
    },
    copyAllMissionsUrl() {
      const inputDom = this.$refs.allMissionsUrl;
      inputDom.select();
      document.execCommand("copy");
    },
    downloadAllMissionsQRCode() {
      const base64QRCodeImage = this.$refs?.allMissionsQrcode?.$el?.src;
      if (base64QRCodeImage) {
        const temporaryDownloadLink = document.createElement("a");
        temporaryDownloadLink.href = base64QRCodeImage;
        temporaryDownloadLink.download = `全任務核銷碼.png`;
        temporaryDownloadLink.style.display = "none";
        document.body.appendChild(temporaryDownloadLink);
        temporaryDownloadLink.click();
      }
    },
  },
};
</script>

<style scoped>
.modal-button-group {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  position: relative;
}

.modal-button-group > .center-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
